import { Component, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { AbstractComponent, Attendance, AttendanceService, AttendanceStatusEnum, Channel, ChannelService, ConfirmationComponent, DateUtil, Department, DepartmentService, ExportService, Pager, Tabulation, TabulationService, TabulationStatus, Team, TeamService, User, UserService } from 'lib-trend-core';
import { concatMap, debounceTime, distinctUntilChanged, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { filterType } from '../report-attendance-list/report-attendance-list.component';

@Component({
    selector: 'app-report-tabulation-attendance',
    templateUrl: './report-tabulation-attendance.v2.component.html',
    // templateUrl: './report-tabulation-attendance.component.html',
    styleUrl: './report-tabulation-attendance.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportTabulationAttendanceComponent extends AbstractComponent implements OnInit {
  private termOfSearch: Subject<string> = new Subject<string>();

  pager: Pager<Attendance> = new Pager<Attendance>({ perPage: 10 });

  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;
  isPicker: boolean = false;

  listDepartment: Array<Department> = new Array<Department>();
  listChannel: Array<Channel> = new Array<Channel>();
  listUser: Array<User> = new Array<User>();
  listTeam: Array<Team> = new Array<Team>();
  listTabulation: Array<Tabulation> = new Array<Tabulation>();
  listTeamChannel: Array<Team> = new Array<Team>();
  listDepartmentChannel: Array<Department> = new Array<Department>();
  listTabulationChannel: Array<Tabulation> = new Array<Tabulation>();
  public attendantsByTeam: Array<any> = new Array<any>();

  labelTabulationStatus: string = 'report.tabulationAttendance.filterTabulations';
  labelDepartment: string = 'report.tabulationAttendance.filterDepartments';
  labelChannel: string = 'report.tabulationAttendance.filterChannels';
  labelUser: string = 'report.tabulationAttendance.colAgent';
  labelPeriod: string = '';

  isLoading: boolean = false;
  existsTabulation: boolean = false;

  attendanceStatusEnum = AttendanceStatusEnum;

  listObservable: Observable<Pager<Attendance>>;
  selectedFilters: filterType = {};

  filteredAttendances: Array<Attendance> = new Array<Attendance>

  breadcrumb = [
    { href: '/report', label: 'report.breadcrumb.reports' },
    { label: 'report.breadcrumb.bcTabulation' },
  ];

  constructor(
    injector: Injector,
    private userService: UserService,
    private departmentService: DepartmentService,
    private channelService: ChannelService,
    private teamService: TeamService,
    private attendanceService: AttendanceService,
    private tabulationService: TabulationService,
    public exportService: ExportService,
    public dialog: MatDialog
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.initializeData();
  }

  private initializeData(): void {
    this.getAttendanceWithTabulationList();
    this.getListChannel();
    this.getListTabulations();

    if (this.isAdmin()) {
      this.getListUser();
      this.getListDepartment();
    } else if (this.isSupervisor() || this.isAttendant()) {
      this.getListTeam();
    }
  }

  private setupForm(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.formGroup = this.formBuilder.group({
      channel: [null],
      department: [null],
      user: [null],
      dateRange: this.formBuilder.group({
        dateStart: [thirtyDaysAgo],
        dateEnd: [today],
      }),
      tabulationStatus: [null],
      tabulationExists: []
    });

    this.labelPeriod = `${DateUtil.toFormart(thirtyDaysAgo, 'dd/MM/yyyy')} - ${DateUtil.toFormart(today, 'dd/MM/yyyy')}`;

    let previousValue = this.formGroup.value;


    this.formGroup.valueChanges
      .pipe(debounceTime(100))
      .subscribe((currentValue) => {
        if (
          previousValue.channel !== currentValue.channel ||
          previousValue.user !== currentValue.user ||
          previousValue.dateRange.dateStart !== currentValue.dateRange.dateStart ||
          previousValue.dateRange.dateEnd !== currentValue.dateRange.dateEnd ||
          previousValue.department !== currentValue.department ||
          previousValue.tabulationStatus !== currentValue.tabulationStatus
        ) {
          this.pager.page = 1;
          this.getAttendanceWithTabulationList();
        }

        previousValue = currentValue;
      })
  }

  private getAttendanceWithTabulationList(): void {
    this.loadingContent = true;

    const formValues = this.formGroup.getRawValue();

    const filters: Record<string, unknown> = {
      status: AttendanceStatusEnum.CLOSED,
      channel: formValues.channel,
      user: formValues.user,
      department: formValues.department,
      ...(formValues.dateRange?.dateStart && formValues.dateRange?.dateEnd
        ? {
          dateStart: formValues.dateRange.dateStart.getTime(),
          dateEnd: formValues.dateRange.dateEnd.getTime(),
        }
        : {}),
      tabulationStatus: formValues.tabulationStatus,
      tabulationExists: true
    };

    this.searchParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    this.attendanceService
      .getReportAttendancesTabulations(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<Attendance>) => {
          this.pager = pager;
          this.setupObservableSearch();
        },
        complete: () => (this.loadingContent = false),
        error: (err) =>
          this.alertService.error(err.error?.message ?? this.t('report.tabulationAttendance.unexpectedError')),
      });
  }


  private setupObservableSearch(): void {
    this.listObservable = this.termOfSearch.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        this.searchString = term
        return this.attendanceService.getReportAttendancesTabulations(this.pager.page, this.pager.perPage, this.searchString, this.searchParams);
      })
    )
    this.listObservable.subscribe({
      next: (pager: Pager<Attendance>) => {
        this.pager = pager;
      },
      complete: () => (this.loadingContent = false),
      error: (err) => this.alertService.error(err.error.message),
    })
  }

  private fetchAttendancesRecursively(attendances: Attendance[], pager: Pager<Attendance>): Observable<Array<Attendance>> {
    return this.attendanceService.getReportAttendancesTabulations(pager.page, pager.perPage, this.searchString, this.searchParams)
      .pipe(
        takeUntil(this.destroy$),
        concatMap(newPager => {
          const allAttendances = [...attendances, ...newPager.list];
          if (allAttendances.length >= newPager.total) {
            return new Observable<Array<Attendance>>(observer => {
              observer.next(allAttendances);
            });
          } else {
            return this.fetchAttendancesRecursively(
              allAttendances,
              { ...pager, page: pager.page + 1 }
            );
          }
        })
      );
  }

  private getListChannel(): void {
    this.channelService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channels: Array<Channel>) => {
          this.listChannel = channels;
        }
      });
  }

  private getListDepartment(): void {
    this.departmentService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (departments: Array<Department>) => {
          this.listDepartment = departments;
        },
      })
  }

  private getListUser(): void {
    this.userService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users: Array<User>) => {
          this.listUser = users;
        },
      })
  }

  private getListTeam(): void {
    this.teamService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (teamList: Array<Team>) => {
          this.listTeam = teamList
            .filter((team: Team) => {
              const userId = this.getCurrentUserUser()._id;
              const isAgent = team.agents.some(
                (agent: User) => agent._id === userId
              )
              const isSupervisor = team.supervisors.some(
                (supervisor: User) => supervisor._id === userId
              )
              return isAgent || isSupervisor;
            })
            .filter(
              (team: Team) => team.department && team.department.removed !== true
            )
        },
      })
  }

  private getListTabulations(): void {
    this.tabulationService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tabList: Array<Tabulation>) => {
          this.listTabulation = tabList
        }
      })

  }

  getTabulationStatusName(tab: TabulationStatus): string {
    return tab?.tabulations?.map(t => t.name).join(', ') || '-';
  }

  getAttendanceDuration(attendance: Attendance): string {
    return this.calculateDuration(attendance.createdAt, attendance.closedAt) ?? '-'
  }

  openPicker(): void {
    this.isPicker = true;
    setTimeout(() => {
      this.picker.open();
    }, 300);
  }

  search(term: string) {
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  searchByDepartment(department: Department): void {
    if (department) {
      this.labelDepartment = department.name;
      this.formGroup.get('department').setValue(department._id);
      if (this.isAdmin()) {
        this.teamService.getList().pipe(takeUntil(this.destroy$)).subscribe((teams: Array<Team>) => {
          this.attendantsByTeam = teams
            .filter((team: Team) => team.department._id === department._id && team.agents?.length)
            .flatMap((team: Team) => team.agents);
        });
      } else if ((this.isSupervisor() || this.isAttendant())) {
        this.attendantsByTeam = this.listTeam
          .filter((team: Team) => team.department._id === department?._id && team.agents?.length)
          .flatMap((team: Team) => team.agents);
      }
    } else {
      this.labelDepartment = 'report.tabulationAttendance.filterDepartments';
      this.formGroup.get('department').setValue(null);
    }

    this.selectedFilters.department = this.labelDepartment !== 'report.tabulationAttendance.filterDepartments'
      ? this.labelDepartment
      : undefined;

    if (!this.selectedFilters.department) {
      delete this.selectedFilters.department;
    }
  }

  searchByChannel(channel: Channel): void {
    if (channel) {
      this.labelChannel = channel.name;
      this.formGroup.get('channel').setValue(channel._id);

      if (this.isSupervisor() || this.isAttendant()) {
        this.listTeamChannel = (this.listTeam ?? [])
          .filter((team: Team) => team.department?.channel?.toString() === channel._id);
        this.listDepartmentChannel = this.listTeamChannel
          .map((team: Team) => team.department)
          .filter((dept: Department, index: number, self: Array<Department>) =>
            !!dept && self.findIndex((d: Department) => d?._id === dept._id) === index);
      } else {
        this.listDepartmentChannel = (this.listDepartment ?? [])
          .filter((dept: Department) => dept.channel?.toString() === channel._id);
      }

      if (this.listTabulation) {
        this.listTabulationChannel = this.listTabulation.filter((tab: Tabulation) => tab.channel?.toString() === channel._id);
      }
    } else {
      this.labelChannel = 'report.tabulationAttendance.filterChannels';
      this.formGroup.get('channel').setValue(null);
      this.listDepartmentChannel = [];
      this.listTeamChannel = [];
      this.listTabulationChannel = [];
    }

    this.selectedFilters.channel = this.labelChannel !== 'report.tabulationAttendance.filterChannels'
      ? this.labelChannel
      : undefined;

    if (!this.selectedFilters.channel) {
      delete this.selectedFilters.channel;
    }

    this.pager.page = 1;
  }

  searchByUser(user: User): void {
    if (user) {
      this.labelUser = user.name;
      this.formGroup.get('user').setValue(user._id);
    } else {
      this.labelUser = 'report.tabulationAttendance.colAgent';
      this.formGroup.get('user').setValue(null);
    }

    this.selectedFilters.user = this.labelUser !== 'report.tabulationAttendance.colAgent'
      ? this.labelUser
      : undefined;

    if (!this.selectedFilters.user) {
      delete this.selectedFilters.user;
    }
  }

  searchByTabulationStatus(tabulation: Tabulation): void {
    if (tabulation) {
      this.labelTabulationStatus = tabulation.name;
      this.formGroup.get('tabulationStatus').setValue(tabulation._id);
    } else {
      this.labelTabulationStatus = 'report.tabulationAttendance.filterTabulations';
      this.formGroup.get('tabulationStatus').setValue(null);
    }

    this.selectedFilters.tabulations = this.labelTabulationStatus !== 'report.tabulationAttendance.filterTabulations'
      ? this.labelTabulationStatus
      : undefined;
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getAttendanceWithTabulationList();
  }

  onPeriodChange(event: MatSelectChange) {
    const today = new Date();
    const period = parseInt(event.value, 10);

    if (!isNaN(period)) {
      if (period === 1) {
        this.formGroup.get('dateRange')?.patchValue({
          dateStart: new Date(),
          dateEnd: new Date(),
        });
      } else if ([7, 15, 30, 60].includes(period)) {
        this.formGroup.get('dateRange')?.patchValue({
          dateStart: new Date(today.setDate(today.getDate() - period)),
          dateEnd: new Date(),
        });
      }
      this.picker?.close();
    }
  }

  onPickerClose() {
    const dateStart = this.formGroup.get('dateRange')?.get('dateStart')?.value;
    const dateEnd = this.formGroup.get('dateRange')?.get('dateEnd')?.value;

    if (dateStart && dateEnd) {
      this.selectedFilters.period = this.t('report.tabulationAttendance.periodRange', {
        start: DateUtil.toFormart(dateStart, 'dd/MM/yyyy'),
        end: DateUtil.toFormart(dateEnd, 'dd/MM/yyyy'),
      });
    } else {
      this.selectedFilters.period = '';
    }
  }
  exportAttendanceReportToExcel(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('report.tabulationAttendance.confirmExportExcel'),
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;
        this.fetchAttendancesRecursively([], new Pager<Attendance>({ perPage: 500 }))
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (attendances: Array<Attendance>) => {
              const formattedData = this.formatAttendanceTabulationData(attendances);
              this.exportService
                .generateExcelAttendanceReport(formattedData, this.selectedFilters, true)
                .then((buffer: any) => this.downloadExcelFile(buffer))
                .finally(() => {
                  this.alertService.success(this.t('report.tabulationAttendance.excelGeneratedSuccess'));
                  this.isLoading = false;
                });
            },
          });
      }
    });
  }

  private formatAttendanceTabulationData(attendances: Attendance[]): any[] {
    return attendances.map((attendance: Attendance) => ({
      createdAt: DateUtil.toFormart(attendance.createdAt, 'dd/MM/yyyy HH:mm:ss'),
      contact: attendance.contact,
      user: attendance?.user,
      channel: attendance?.channel,
      department: attendance.department,
      tabulations: attendance.tabulationStatus?.tabulations.map((tab: Tabulation) => tab.name).join(', '),
      observations: attendance.tabulationStatus?.description,
      duration: this.getAttendanceDuration(attendance),
    }));
  }


  private downloadExcelFile(buffer: any): void {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Trendmulti_Relatório_Atendimentos_tabulatos_${new Date().toLocaleDateString()}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  exportAttendanceReportToPdf(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('report.tabulationAttendance.confirmExportPdf'),
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;
        this.fetchAttendancesRecursively([], new Pager<Attendance>({ perPage: 500 }))
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (attendances: Array<Attendance>) => {
              const formattedData = this.formatAttendanceTabulationData(attendances);
              this.exportService
                .generatePDFAttendanceReport(formattedData, this.selectedFilters, true)
                .then(() => this.alertService.success(this.t('report.tabulationAttendance.pdfGeneratedSuccess')))
                .finally(() => {
                  this.isLoading = false;
                });
            },
          });
      }
    });
  }

  private calculateDuration(startDate: Date, endDate: Date): string {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const duration = end - start;

    const days = Math.floor(duration / (1000 * 60 * 60 * 24));
    const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((duration / (1000 * 60)) % 60);

    if (days > 0) {
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }

    return `${hours}h ${minutes}m`;
  }
}
